<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ArrowIcon from '@/components/shared/ArrowIcon.vue'
import type { Registration, RegistrationFormData, PriceBreakdownItem } from '@/types/registration'

const props = defineProps<{
  registration: Registration
  registrationData: RegistrationFormData
}>()

const { t } = useI18n()

function formatCurrency(amount: number): string {
  return `EUR ${amount.toFixed(2)}`
}

// Derive indicative prices from the price_breakdown snapshot, since financial
// amount fields are intentionally zeroed for waiting list registrations.
const breakdown = computed(() => props.registration.price_breakdown)

function sumByTypes(types: string[]): number {
  return breakdown.value
    .filter((item: PriceBreakdownItem) => types.includes(item.type))
    .reduce((sum: number, item: PriceBreakdownItem) => sum + parseFloat(item.amount), 0)
}

const indicativeBasePrice = computed(() => sumByTypes(['base_price']))
const indicativeOptionsTotal = computed(() => sumByTypes(['option', 'checkbox']))
const indicativeVoucherDiscount = computed(() => sumByTypes(['voucher_discount']))
const indicativeOptionItems = computed(() =>
  breakdown.value.filter((item: PriceBreakdownItem) => item.type === 'option' || item.type === 'checkbox'),
)
const indicativeTotal = computed(
  () => indicativeBasePrice.value + indicativeOptionsTotal.value - indicativeVoucherDiscount.value,
)
</script>

<template>
  <div class="space-y-6">
    <!-- Success banner -->
    <div class="rounded-2xl bg-emerald-50 border border-emerald-200 px-6 py-8 text-center">
      <div class="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 class="text-2xl font-bold text-emerald-800 mb-1">
        {{ t('registration.waiting_list.confirm_heading') }}
      </h2>
      <p class="text-sm text-emerald-700 mb-3">
        {{ t('registration.waiting_list.confirm_message') }}
      </p>
      <p class="text-lg font-bold text-emerald-800">
        {{ t('registration.waiting_list.position_label') }}:
        <span class="font-black">#{{ registration.waiting_list_position }}</span>
      </p>
    </div>

    <!-- Race summary -->
    <div class="bg-slate-50 rounded-xl border border-slate-200 px-5 py-4 space-y-1">
      <p class="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
        {{ registrationData.race.name }}
      </p>
      <p class="text-sm text-slate-700">
        {{ t('registration.waiting_list.contact_message') }}
      </p>
    </div>

    <!-- Indicative price breakdown -->
    <div class="rounded-xl border border-slate-200 overflow-hidden">
      <div class="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <h3 class="text-sm font-semibold text-slate-700">
          {{ t('registration.waiting_list.indicative_price_label') }}
        </h3>
      </div>
      <div class="px-5 py-4 space-y-3">
        <!-- Base price -->
        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-600">{{ t('registration.payment.base_price_label') }}</span>
          <span class="font-medium text-slate-900">{{ formatCurrency(indicativeBasePrice) }}</span>
        </div>

        <!-- Options total (only when > 0) -->
        <div
          v-if="indicativeOptionsTotal > 0"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-slate-600">{{ t('registration.payment.options_total_label') }}</span>
          <span class="font-medium text-slate-900">{{ formatCurrency(indicativeOptionsTotal) }}</span>
        </div>

        <!-- Option / checkbox line items -->
        <div
          v-for="(item, i) in indicativeOptionItems"
          :key="i"
          class="flex items-center justify-between text-xs text-slate-500 pl-3"
        >
          <span>{{ item.type === 'option' ? `${item.field_label}: ${item.option_label}` : item.field_label }}</span>
          <span>+ EUR {{ parseFloat(item.amount).toFixed(2) }}</span>
        </div>

        <!-- Voucher discount (only when > 0) -->
        <div
          v-if="indicativeVoucherDiscount > 0"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-slate-600">{{ t('registration.payment.voucher_discount_label') }}</span>
          <span class="font-medium text-emerald-600">- {{ formatCurrency(indicativeVoucherDiscount) }}</span>
        </div>

        <!-- Divider + total -->
        <div class="border-t border-slate-200 pt-3 flex items-center justify-between">
          <span class="text-sm font-bold text-slate-800">{{ t('registration.waiting_list.total_if_promoted') }}</span>
          <span class="text-lg font-bold text-emerald-600">{{ formatCurrency(indicativeTotal) }}</span>
        </div>
      </div>
    </div>

    <!-- View confirmation link -->
    <div class="flex justify-center">
      <RouterLink
        :to="{ name: 'participant.races' }"
        class="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 underline underline-offset-2 transition-colors"
      >
        {{ t('registration.confirmation.view_registrations') }}
        <ArrowIcon direction="right" class="w-4 h-4" />
      </RouterLink>
    </div>
  </div>
</template>
